import { Company } from '@ankor-io/iam/types'

import { AuthenticationContext, PrincipalIdentity } from '@/iam/types'

export interface ServerMixpanelProperties {
  yacht?: string
  yachtUri?: string
  caCompanyUri?: string
  caCompany?: string
  caContact?: string
  raCompany?: string
  raCompanyUri?: string
  timestamp?: string
  contactType?: string
  eventSource?: string
  post?: string
  postUri?: string
  postingCompanyUri?: string
  postingCompany?: string
  respondingCompanyUri?: string
  respondingCompany?: string
  [key: string]: any
}

export class ServerMixpanelClient {
  private authenticationContext: AuthenticationContext | null
  private token: string
  private distinctId: string
  private baseUrl: string

  constructor(
    authenticationContext: AuthenticationContext | null,
    token: string,
    distinctId: string = 'anonymous',
    baseUrl: string = '/api/analytics/mixpanel',
  ) {
    this.authenticationContext = authenticationContext
    this.token = token
    this.distinctId = distinctId
    this.baseUrl = baseUrl
  }

  /**
   * Create a new client instance with updated user info
   */
  static createClient(
    authenticationContext: AuthenticationContext | null,
    token: string,
    user: PrincipalIdentity | null,
    company: Company | null,
    baseUrl?: string,
  ): ServerMixpanelClient {
    const distinctId = user?.id || 'anonymous'
    const client = new ServerMixpanelClient(authenticationContext, token, distinctId, baseUrl)

    // Set initial people properties if user is available
    if (user) {
      client
        .peopleSet({
          $email: user.email,
          $name: `${user.given_name} ${user.family_name}`,
          $id: user.id,
          $companyId: company?.id,
          $companyName: company?.name,
        })
        .catch(console.error)
    }

    return client
  }

  /**
   * Track an event
   */
  async track(event: string, properties?: ServerMixpanelProperties): Promise<void> {
    try {
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      }

      if (this.authenticationContext) {
        const token = await this.authenticationContext.getToken()
        if (token) {
          headers['Authorization'] = `Bearer ${token}`
        }
      }

      const response = await fetch(`${this.baseUrl}/track`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          event,
          properties,
          distinct_id: this.distinctId,
          token: this.token,
        }),
      })

      if (!response.ok) {
        const error = await response.json()
        console.error('Failed to track event:', error)
      }
    } catch (error) {
      console.error('Error tracking event:', error)
    }
  }

  /**
   * Identify a user
   */
  async identify(distinctId: string, properties?: Record<string, any>): Promise<void> {
    try {
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      }

      if (this.authenticationContext) {
        const token = await this.authenticationContext.getToken()
        if (token) {
          headers['Authorization'] = `Bearer ${token}`
        }
      }

      const response = await fetch(`${this.baseUrl}/identify`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          distinct_id: distinctId,
          properties,
          token: this.token,
        }),
      })

      if (!response.ok) {
        const error = await response.json()
        console.error('Failed to identify user:', error)
      }
    } catch (error) {
      console.error('Error identifying user:', error)
    }
  }

  /**
   * Set people properties
   */
  async peopleSet(properties: Record<string, any>): Promise<void> {
    try {
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      }

      if (this.authenticationContext) {
        const token = await this.authenticationContext.getToken()
        if (token) {
          headers['Authorization'] = `Bearer ${token}`
        }
      }

      const response = await fetch(`${this.baseUrl}/people/set`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          distinct_id: this.distinctId,
          properties,
          token: this.token,
        }),
      })

      if (!response.ok) {
        const error = await response.json()
        console.error('Failed to set people properties:', error)
      }
    } catch (error) {
      console.error('Error setting people properties:', error)
    }
  }
}
